import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';
import { useCoins } from '@/shared/api';
import type { CryptocurrencyList, QuoteCurrency } from './types';

export const useTradeTopCryptocurrencies = (quote: QuoteCurrency = 'usdt') => {
  const coins = useCoins();
  const { t } = useLingui();

  const data = useMemo((): CryptocurrencyList[] | undefined => {
    if (!coins.data) return undefined;

    const availableCoins = coins.data.filter((coin) => coin.on_market === true);
    const getPrice = (coin: (typeof availableCoins)[number]) =>
      quote === 'usdt' ? coin.price_usdt : coin.price_iqd;

    const topGainers = [...availableCoins]
      .sort((a, b) => {
        const aChange = parseFloat(a.price_change_percent || '0');
        const bChange = parseFloat(b.price_change_percent || '0');
        return bChange - aChange;
      })
      .slice(0, 5)
      .map((coin) => ({
        symbol: coin.symbol,
        name: coin.name,
        price: getPrice(coin),
        change: parseFloat(coin.price_change_percent || '0'),
        image: coin.image,
        slug: coin.slug,
      }));

    const topVolume = [...availableCoins]
      .sort((a, b) => {
        const aVolume = parseFloat(a.volume_usdt || '0');
        const bVolume = parseFloat(b.volume_usdt || '0');
        return bVolume - aVolume;
      })
      .slice(0, 5)
      .map((coin) => ({
        symbol: coin.symbol,
        name: coin.name,
        price: getPrice(coin),
        change: parseFloat(coin.price_change_percent || '0'),
        image: coin.image,
        slug: coin.slug,
      }));

    const mostPopular = [...availableCoins]
      .sort((a, b) => {
        const aPrice = a.popularity ?? 0;
        const bPrice = b.popularity ?? 0;
        return bPrice - aPrice;
      })
      .slice(0, 5)
      .map((coin) => ({
        symbol: coin.symbol,
        name: coin.name,
        price: getPrice(coin),
        change: parseFloat(coin.price_change_percent || '0'),
        image: coin.image,
        slug: coin.slug,
      }));

    return [
      {
        title: t`Top Gainer`,
        subtitle: t`Most Profitable in last 24H`,
        icon: 'up',
        items: topGainers,
      },
      {
        title: t`High Volume`,
        subtitle: t`Highest Volume in last 24H`,
        icon: 'flame',
        items: topVolume,
      },
      {
        title: t`Most Popular`,
        subtitle: t`Most Popular on IQX`,
        icon: 'star',
        items: mostPopular,
      },
    ];
  }, [coins.data, quote, t]);

  return {
    data,
    isLoading: coins.isLoading,
    error: coins.error,
  };
};
